import * as _ from "lodash"
import {isSameMonth, parseISO, startOfMonth} from "date-fns"
import {cubejsApi} from "@/cube/cubejsApi"
import {MemberSnapshotCube} from "@/cube"
import {CampaignService} from "@/CampaignService"
import {DateRangeSelector} from "@/DateRangeSelector"

export class DateRangeService {
  static readonly DEFAULT_OPTION_LABEL = "Since first campaign"
  static readonly CAMPAIGN_OPTION_LABEL = "Since last campaign"
  static readonly PLAN_OPTION_LABEL = "Since plan start"

  dateRangeSelector?: DateRangeSelector = <any>null

  private readonly cache = new Map<string, DateRangeSelector>()

  constructor(private readonly campaignService: CampaignService) {
    // Empty
  }

  private static getQuery(planId: string) {
    return <const>{
      timeDimensions: [
        {
          dimension: MemberSnapshotCube._at,
          granularity: "month",
        },
      ],
      filters: [
        {
          dimension: "Member.planId",
          operator: "equals",
          values: [planId],
        },
      ],
    }
  }

  async setPlan(planId: string): Promise<void> {
    this.dateRangeSelector = await this.getDateRangeSelector(planId)
  }

  reset(): void {
    this.cache.clear()
  }

  private async getDateRangeSelector(
    planId: string
  ): Promise<DateRangeSelector> {
    let dateRangeSelector = this.cache.get(planId)
    if (dateRangeSelector == null) {
      dateRangeSelector = await this.createDateRangeSelector(planId)
      this.cache.set(planId, dateRangeSelector)
    }

    return dateRangeSelector
  }

  private async createDateRangeSelector(planId: string) {
    const queryResult = (
      await cubejsApi.load(DateRangeService.getQuery(planId))
    ).loadResponse.results[0]

    console.log("Result", queryResult)

    // FIXME The query is returning null on the first entry
    //  (unless there is only 1) bypass cube for this?
    const periodStart = parseISO(
      _.isNull(queryResult.data[0][MemberSnapshotCube._atMonth])
        ? queryResult.data[1][MemberSnapshotCube._atMonth]
        : queryResult.data[0][MemberSnapshotCube._atMonth]
    )

    const periodEnd = parseISO(
      _.last(queryResult.data)![MemberSnapshotCube._atMonth]
    )

    const dateRangeSelector = new DateRangeSelector({
      start: periodStart,
      end: periodEnd,
    })

    let selectedStart = periodStart

    const campaigns = (await this.campaignService.getCampaigns(planId)).filter(
      campaign => periodStart <= startOfMonth(parseISO(campaign!.date))
    )

    const firstCampaign = _.last(campaigns)

    if (firstCampaign) {
      if (campaigns.length > 1) {
        const latestCampaign = _.first(campaigns)
        let lastCampaignDate = startOfMonth(parseISO(latestCampaign!.date))

        // When the campaign is too recent, pick an earlier period
        if (isSameMonth(lastCampaignDate, periodEnd) && campaigns.length > 1) {
          lastCampaignDate = startOfMonth(parseISO(campaigns[1].date))
        }

        dateRangeSelector.preDefinedOptions.push({
          label: DateRangeService.CAMPAIGN_OPTION_LABEL,
          value: {
            start: lastCampaignDate,
            end: periodEnd,
          },
        })
      }

      selectedStart = startOfMonth(parseISO(firstCampaign.date))
      dateRangeSelector.preDefinedOptions.push({
        label: DateRangeService.DEFAULT_OPTION_LABEL,
        value: {
          start: selectedStart,
          end: periodEnd,
        },
      })
    }

    dateRangeSelector.preDefinedOptions.push({
      label: DateRangeService.PLAN_OPTION_LABEL,
      value: {
        start: periodStart,
        end: periodEnd,
      },
    })

    dateRangeSelector.selected = {
      start: selectedStart,
      end: periodEnd,
    }

    return dateRangeSelector
  }
}
